import { ErrorMessage } from 'components/Widgets/ErrorMessage';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import useLocation from 'hooks/useLocation';
import { useCallback, useMemo, useState } from 'react';
import { getApiErrorMessage } from 'services/_infra/apiError.utils';
import { useGetEarthquakesQuery } from 'services/weather/weather.api';
import {
  WeatherMapStaleDataNotice,
  WeatherMapUpdatingStatus,
} from '../../components/WeatherMapDataStatus/WeatherMapDataStatus';
import WeatherMapSegmentedControl from '../../components/WeatherMapSegmentedControl/WeatherMapSegmentedControl';
import '../../features/Earthquakes/assets/earthquakes.scss';
import EarthquakeInformationPanel from '../../features/Earthquakes/components/EarthquakeInformationPanel/EarthquakeInformationPanel';
import EarthquakeMapContent from '../../features/Earthquakes/components/EarthquakeMapContent/EarthquakeMapContent';
import {
  EARTHQUAKES_QUERY_OPTIONS,
  EARTHQUAKES_REFRESH_INTERVAL_MINUTES,
  EARTHQUAKES_ROUTE,
} from '../../features/Earthquakes/config/earthquakes.config';
import { resolveWeatherMapWidgetLayout } from '../../utils/weatherMapWidgetLayout.utils';
import WeatherMapWidgetEmptyState from '../components/WeatherMapWidgetEmptyState/WeatherMapWidgetEmptyState';
import WeatherMapWidgetLayout from '../components/WeatherMapWidgetLayout/WeatherMapWidgetLayout';
import { EARTHQUAKES_WIDGET_VIEW_OPTIONS } from './config/earthquakesWidgetView.config';

const EarthquakesWidget = ({ layout, dimensions, containerSizePx }) => {
  const { locations } = useLocation();
  const [compactView, setCompactView] = useState('map');
  const [selectedEarthquakeId, setSelectedEarthquakeId] = useState(null);
  const {
    data: queryEarthquakes = [],
    error,
    isError,
    isFetching,
    isLoading,
    refetch,
  } = useGetEarthquakesQuery(undefined, EARTHQUAKES_QUERY_OPTIONS);
  const earthquakes = Array.isArray(queryEarthquakes) ? queryEarthquakes : [];
  const selectedEarthquake = useMemo(
    () =>
      earthquakes.find((earthquake) => earthquake.id === selectedEarthquakeId) ??
      earthquakes[0] ??
      null,
    [earthquakes, selectedEarthquakeId],
  );
  const selectEarthquake = useCallback((earthquake) => {
    setSelectedEarthquakeId(earthquake?.id ?? null);
  }, []);
  const widgetLayout = resolveWeatherMapWidgetLayout({
    layout,
    containerWidthPx: containerSizePx?.width ?? null,
  });
  const isCompactSinglePanel = widgetLayout === '1x1';

  if (isLoading) {
    return <WidgetLoadingAnimation widgetName="Earthquake" dimensions={dimensions} />;
  }

  if (isError && !earthquakes.length) {
    return <ErrorMessage message={getApiErrorMessage(error)} onRetry={refetch} />;
  }

  if (!earthquakes.length) {
    return (
      <WeatherMapWidgetEmptyState
        ariaLabel="Earthquake widget"
        title="Recent Earthquakes"
        message="No recent earthquake data is available right now."
      />
    );
  }

  const mapContent = (
    <EarthquakeMapContent
      earthquakes={earthquakes}
      selectedEarthquake={selectedEarthquake}
      fallbackCenter={locations.effectiveLocation?.coords}
      onSelectEarthquake={selectEarthquake}
      scrollZoom
    />
  );
  const informationContent = (
    <EarthquakeInformationPanel
      earthquakes={earthquakes}
      selectedEarthquake={selectedEarthquake}
      isRefreshing={isFetching && !isLoading}
      layoutVariant="widget"
      onSelectEarthquake={selectEarthquake}
    />
  );
  const isSideBySide = widgetLayout === '2x1';

  return (
    <section
      aria-label="Earthquake widget"
      className="earthquakes earthquakes-widget d-flex h-100 min-h-0 w-100 flex-column gap-1 overflow-hidden"
    >
      <WidgetTimeUpdate
        route={EARTHQUAKES_ROUTE}
        updateIntervalBasis={EARTHQUAKES_REFRESH_INTERVAL_MINUTES}
        compact
      />

      {isError && (
        <WeatherMapStaleDataNotice
          message="Update failed. Showing the most recently available earthquake data."
          compact
        />
      )}

      {isCompactSinglePanel ? (
        <div className="d-flex min-h-0 flex-grow-1 flex-column gap-2 overflow-hidden">
          <WeatherMapSegmentedControl
            ariaLabel="Earthquake widget view"
            options={EARTHQUAKES_WIDGET_VIEW_OPTIONS}
            value={compactView}
            onChange={setCompactView}
          />

          {compactView === 'map' ? (
            <section aria-label="Earthquake map" className="d-flex min-h-0 flex-grow-1">
              {mapContent}
            </section>
          ) : (
            <section
              aria-label="Earthquake details and recent events"
              className="d-flex min-h-0 flex-grow-1 overflow-hidden rounded-2 border"
              tabIndex={0}
            >
              {informationContent}
            </section>
          )}
        </div>
      ) : (
        <WeatherMapWidgetLayout
          layout={widgetLayout}
          mapAriaLabel="Earthquake map"
          informationAriaLabel="Earthquake details and recent events"
          mapClassName={isSideBySide ? undefined : 'h-50 flex-grow-1'}
          informationClassName={isSideBySide ? 'w-50' : 'h-50'}
          informationTabIndex={0}
          mapContent={mapContent}
          informationContent={informationContent}
        />
      )}

      {isFetching && !isLoading && (
        <WeatherMapUpdatingStatus message="Updating recent earthquake data" />
      )}
    </section>
  );
};

export default EarthquakesWidget;
